import type { Context as KoaContext } from 'koa'
import { buildUserKey, extractIp } from './helpers'
import { createRateLimiter } from './middleware'
import type { KoaRateLimiterInstance } from './middleware'
import type { RateLimitOptions } from '../../types'

// ── Strategy aliases ───────────────────────────────────────────────────────────

export const createFixedRateLimiter = (
  options: Omit<RateLimitOptions<KoaContext>, 'strategy'> = {}
): KoaRateLimiterInstance =>
  createRateLimiter({ ...options, strategy: 'fixed' })

export const createSlidingRateLimiter = (
  options: Omit<RateLimitOptions<KoaContext>, 'strategy'> = {}
): KoaRateLimiterInstance =>
  createRateLimiter({ ...options, strategy: 'sliding' })

// ── User-scoped factories ──────────────────────────────────────────────────────

/**
 * Rate limiter keyed by `ctx.state.user.id` (or `._id`),
 * falling back to the client IP when unauthenticated.
 */
export const createUserRateLimiter = (
  options: RateLimitOptions<KoaContext> = {}
): KoaRateLimiterInstance =>
  createRateLimiter({ ...options, keyGenerator: buildUserKey })

export const createSlidingUserRateLimiter = (
  options: Omit<RateLimitOptions<KoaContext>, 'strategy'> = {}
): KoaRateLimiterInstance =>
  createRateLimiter({
    ...options,
    strategy: 'sliding',
    keyGenerator: buildUserKey
  })

// ── Endpoint-scoped factories ──────────────────────────────────────────────────

export const createEndpointRateLimiter = (
  endpoint: string,
  options: RateLimitOptions<KoaContext> = {}
): KoaRateLimiterInstance =>
  createRateLimiter({
    ...options,
    keyGenerator: (ctx: KoaContext) => {
      const base = options.keyGenerator
        ? options.keyGenerator(ctx)
        : extractIp(ctx)
      return `${endpoint}:${base}`
    }
  })

export const createSlidingEndpointRateLimiter = (
  endpoint: string,
  options: Omit<RateLimitOptions<KoaContext>, 'strategy'> = {}
): KoaRateLimiterInstance =>
  createRateLimiter({
    ...options,
    strategy: 'sliding',
    keyGenerator: (ctx: KoaContext) => {
      const base = options.keyGenerator
        ? options.keyGenerator(ctx)
        : extractIp(ctx)
      return `${endpoint}:${base}`
    }
  })
